import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL, securityHeaders } from "../Constants";
import SessionGuard from "../components/Molecules/Session/SessionGuard";
import {
  EXPIRY_REASON,
  installSessionInterceptor,
  notifySessionExpired,
} from "../services/session";

/**
 * Gate for every `/home` screen, and the mount point for `SessionGuard`.
 *
 * WHAT CHANGED HERE
 *
 * `fetchValidation` used to `catch` EVERY error and remove the token — so a
 * dropped Wi-Fi connection, a CORS hiccup or a 500 silently signed the operator
 * out, with a `toastVisible` flag that was set but never rendered anywhere, so
 * they were never told. Only a 401 means "your session is over"; anything else
 * is a transient failure and must not end the session.
 *
 * The 401 itself is now reported through `notifySessionExpired`, which
 * `SessionGuard` turns into a sign-in dialog over the current page rather than
 * a silent bounce to `/`.
 */
const PrivateRoutes = () => {
  const location = useLocation();
  const auth = localStorage.getItem("token");

  useEffect(() => {
    if (!auth) return;

    // Install before the validation request so an access token that expired
    // while the app was closed can refresh and retry instead of immediately
    // falling through to the credential dialog.
    installSessionInterceptor();

    let cancelled = false;

    const validate = async () => {
      try {
        await axios.get(`${API_URL}/api/RolePage/ValidateToken`, {
          headers: securityHeaders,
        });
      } catch (error) {
        if (cancelled) return;

        // Only an explicit 401 ends the session. A network error has no
        // `response` at all and must be left alone — signing someone out
        // because their connection blinked is both wrong and baffling.
        if (error?.response?.status === 401) {
          notifySessionExpired(EXPIRY_REASON.UNAUTHORIZED);
        }
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [location, auth]);

  if (!auth) return <Navigate to="/" />;

  return (
    <SessionGuard>
      <Outlet />
    </SessionGuard>
  );
};

export default PrivateRoutes;
